import { useCallback, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useShallow } from 'zustand/react/shallow';
// mantine
import { Pagination } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
// components
import type { ITopicsParams, ITopicListItem } from '@newo/api/gateway';
import AKBHeader from '@/pages/AKB/components/AKBHeader';
import AKBDataTable from '@/pages/AKB/components/AKBDataTable';
import Toolbar from '@/pages/AKB/components/Toolbar';
import ScrollToTopButton from '@/components/SctollToTopButton';
// utils
import useAKBStore from '@/pages/AKB/AKB.store.ts';
import { openConfirmDeleteModal } from '@/utils/ui.tsx';
import {
    useTopicsQuery,
    useDeleteTopicsMutation,
    useDeletePersonaAKBMutation
} from '@/services/akb/akb.queries.ts';
import { PageContainer } from '@/components/PageContainer/PageContainer.tsx';

const DEBOUNCE_TIMEOUT = 400;

const AKBPage = () => {
    const {
        scoreThreshold,
        persona: selectedPerson,
        sortStatus
    } = useAKBStore(
        useShallow((state) => ({
            scoreThreshold: state.scoreThreshold,
            persona: state.persona,
            sortStatus: state.sortStatus
        }))
    );

    const personaId = selectedPerson?.id ?? '';
    const [params, setParams] = useState<ITopicsParams>({
        persona_id: '',
        page: 1,
        per: 25
    });
    const [selectedTopics, setSelectedTopics] = useState<ITopicListItem[]>([]);

    useEffect(() => {
        setParams((prev) => ({
            ...prev,
            ...(prev.fuzzy_search_query && {
                fuzzy_search_score_threshold: scoreThreshold
            }),
            order_by: `${sortStatus.direction === 'desc' ? '-' : ''}${String(sortStatus.columnAccessor)}`,
            persona_id: personaId
        }));
    }, [personaId, sortStatus, scoreThreshold]);

    const [debouncedParams] = useDebouncedValue(params, DEBOUNCE_TIMEOUT);
    const { data, refetch, isFetching } = useTopicsQuery(debouncedParams);

    const topics = data?.items ?? [];

    const deleteTopicsMutation = useDeleteTopicsMutation();
    const { mutate: deletePersonaAKB } = useDeletePersonaAKBMutation();

    const handleAKBDelete = () => {
        openConfirmDeleteModal({
            title: 'Delete AKB?',
            children: 'Are you sure you want to delete AKB?',
            confirmLabel: 'Delete AKB',
            onConfirm: () => {
                deletePersonaAKB(
                    { personaId },
                    {
                        onSuccess() {
                            setSelectedTopics([]);
                            refetch();
                        }
                    }
                );
            }
        });
    };

    const handleTopicsDelete = () => {
        openConfirmDeleteModal({
            title: 'Delete Topics?',
            children: 'Are you sure you want to delete selected topics?',
            confirmLabel: 'Delete topics',
            onConfirm: () => {
                const ids = selectedTopics.map(({ topic }) => topic.topic_id);
                deleteTopicsMutation.mutate(
                    { topic_ids: ids },
                    {
                        onSuccess() {
                            setSelectedTopics([]);
                            refetch();
                        }
                    }
                );
            }
        });
    };

    const handlePaginationChange = useCallback((page: number) => {
        setParams((prev) => ({ ...prev, page }));
    }, []);

    const handleParamsChange = useCallback(
        (newParams: Partial<ITopicsParams>) => {
            setParams((prev) => ({ ...prev, ...newParams, page: 1 }));
        },
        []
    );

    const handleSearchChange = useCallback(
        (searchTerm: string) => {
            handleParamsChange({
                fuzzy_search_query: searchTerm,
                ...(searchTerm
                    ? {
                          fuzzy_search_score_threshold: scoreThreshold,
                          order_by: 'search_score'
                      }
                    : {
                          fuzzy_search_score_threshold: undefined,
                          order_by: 'created_at'
                      })
            });
        },
        [handleParamsChange, scoreThreshold]
    );

    const handleRefetch = () => {
        refetch();
    };

    return (
        <div className="h-full w-full">
            <Helmet>
                <title>NEWO - AKB</title>
            </Helmet>
            <PageContainer className="px-0 h-full">
                <AKBHeader />
                <div className="px-4 flex flex-col h-full">
                    <Toolbar
                        hasSelectedItems={selectedTopics.length > 0}
                        hasTopics={topics.length > 0}
                        searchTerm={params.fuzzy_search_query}
                        onAKBDelete={handleAKBDelete}
                        onSearchChange={handleSearchChange}
                        onTopicsDelete={handleTopicsDelete}
                    />

                    <AKBDataTable
                        className="flex-1"
                        isFetching={isFetching}
                        items={topics}
                        params={params}
                        personaId={personaId}
                        refetch={handleRefetch}
                        selectedTopics={selectedTopics}
                        setSelectedTopic={setSelectedTopics}
                        onParamsChange={handleParamsChange}
                    />
                    {topics.length > 0 && (
                        <div className="flex justify-center pb-10 pt-2">
                            <Pagination
                                mt="sm"
                                total={
                                    data?.metadata
                                        ? Math.ceil(
                                              data.metadata.total /
                                                  data.metadata.per
                                          )
                                        : 1
                                }
                                value={params.page}
                                onChange={handlePaginationChange}
                            />
                        </div>
                    )}
                </div>
            </PageContainer>
            <ScrollToTopButton />
        </div>
    );
};

export default AKBPage;
